import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Slider from '@mui/material/Slider';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import InputBase from '@mui/material/InputBase';
import { Icon } from '@iconify/react';

/** One EvoWalk sample. 64 Hz → 15.625 ms. */
const SAMPLE_MS = 15.625;
const MIN_SEC = -3;
const MAX_SEC = 3;
const STEP_SEC = SAMPLE_MS / 1000;

function formatOffsetMs(offsetSec) {
  const ms = Math.round(offsetSec * 1000);
  return ms >= 0 ? `+${ms}` : String(ms);
}

function snapTypedMs(rawMs) {
  const steps = Math.round(rawMs / SAMPLE_MS);
  const maxSteps = Math.round((MAX_SEC * 1000) / SAMPLE_MS);
  const clampedSteps = Math.max(-maxSteps, Math.min(maxSteps, steps));
  return clampedSteps * STEP_SEC;
}

function SensorOffsetSlider({
  label,
  offsetSec,
  onOffsetChange,
  onReset,
  disabled = false,
}) {
  const [offsetInputValue, setOffsetInputValue] = useState('0');

  useEffect(() => {
    setOffsetInputValue(formatOffsetMs(offsetSec));
  }, [offsetSec]);

  const commitOffsetInput = () => {
    const raw = parseFloat(offsetInputValue);
    if (isNaN(raw)) {
      setOffsetInputValue(formatOffsetMs(offsetSec));
      return;
    }
    onOffsetChange(snapTypedMs(raw));
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'wrap',
        width: '100%',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 200 }}>
        <Tooltip
          title="Residual tweak vs video t=0. 0 shows the served sensor series. Device sync is already in that series, so this control starts at 0 and is not applied again. Earlier (−) or later (+) moves the sensor series only."
          placement="top"
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'default' }}>
            <Icon icon="mdi:timer-sync-outline" style={{ fontSize: '1.2rem', color: '#63C7CF' }} />
            <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
              {label}
            </Typography>
          </Box>
        </Tooltip>
        <Tooltip title="Type a value in ms and press Enter. Snaps to the 15.625 ms sensor sample and shows the nearest millisecond." placement="top">
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <InputBase
              value={offsetInputValue}
              onChange={(e) => setOffsetInputValue(e.target.value)}
              onBlur={commitOffsetInput}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  commitOffsetInput();
                  e.target.blur();
                }
              }}
              disabled={disabled}
              inputProps={{ 'aria-label': `${label} in ms` }}
              sx={{
                width: 72,
                fontVariantNumeric: 'tabular-nums',
                fontSize: '0.875rem',
                fontWeight: offsetSec !== 0 ? 600 : 400,
                color: offsetSec === 0 ? 'text.secondary' : '#63C7CF',
                border: '1px solid',
                borderColor: offsetSec === 0 ? '#ccc' : '#63C7CF',
                borderRadius: 1,
                px: 0.75,
                py: 0.25,
                '& input': { textAlign: 'right', padding: 0 },
                '&.Mui-focused': { borderColor: '#63C7CF', boxShadow: '0 0 0 2px rgba(99,199,207,0.25)' },
              }}
            />
            <Typography variant="body2" sx={{ ml: 0.5, color: 'text.secondary', fontSize: '0.8rem' }}>
              ms
            </Typography>
          </Box>
        </Tooltip>
      </Box>

      <Box sx={{ flex: 1, minWidth: 160 }}>
        <Slider
          value={offsetSec}
          min={MIN_SEC}
          max={MAX_SEC}
          step={STEP_SEC}
          disabled={disabled}
          onChange={(_, val) => onOffsetChange(val)}
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => `${formatOffsetMs(v)}ms`}
          sx={{
            color: '#63C7CF',
            '& .MuiSlider-thumb': { width: 16, height: 16 },
          }}
        />
      </Box>

      <Button
        size="small"
        variant="outlined"
        onClick={onReset}
        disabled={disabled || offsetSec === 0}
        sx={{ minWidth: 64, textTransform: 'none', borderColor: '#ccc', color: 'text.secondary' }}
      >
        Reset
      </Button>
    </Box>
  );
}

export default SensorOffsetSlider;
